import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, X } from 'lucide-react'
import { getRequestTypes, createEmployeeRequest, type RequestTypeConfig } from '@/api/requests'
import { createOvertime, createRegularization } from '@/api/attendanceRequests'
import { getMyHistory } from '@/api/attendance'
import { useAuthStore } from '@/stores/authStore'
import { typeMeta } from './requestMeta'

const inputCls = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand'

function today() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** "New Request": pick a type, then fill its short form. Leave and WFH hand over to
 *  the existing Apply for Leave modal via `onLeave`. */
export default function NewRequestModal({ onClose, onLeave, initialKind }: {
  onClose: () => void
  onLeave: (type: 'leave' | 'wfh') => void
  initialKind?: string
}) {
  const { data } = useQuery({ queryKey: ['request-types'], queryFn: getRequestTypes, staleTime: 60_000 })
  const types = data?.types || []
  const [kind, setKind] = useState<string | null>(initialKind || null)
  const cfg = types.find((t) => t.key === kind)

  const pick = (t: RequestTypeConfig) => {
    if (t.key === 'leave' || t.key === 'wfh') { onLeave(t.key); return }
    setKind(t.key)
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            {cfg && (
              <button onClick={() => setKind(null)} className="text-gray-400 hover:text-gray-700" title="Back">
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <h2 className="text-lg font-bold text-gray-900">{cfg ? cfg.label : 'New Request'}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        {!cfg ? (
          <div className="p-5 space-y-5">
            {!data && <p className="text-sm text-gray-400">Loading…</p>}
            {data?.categories.map((c) => {
              const list = types.filter((t) => t.category === c.key)
              if (!list.length) return null
              return (
                <div key={c.key}>
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">{c.label}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {list.map((t) => {
                      const { icon: Icon, tint } = typeMeta(t.key)
                      return (
                        <button key={t.key} onClick={() => pick(t)}
                          className="flex items-start gap-3 rounded-lg border border-gray-100 p-3 text-left hover:border-brand/40 hover:bg-brand/5 transition-colors">
                          <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${tint}`}><Icon className="h-[18px] w-[18px]" /></span>
                          <span>
                            <span className="block text-sm font-medium text-gray-900">{t.label}</span>
                            <span className="block text-xs text-gray-500">{t.description}</span>
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <RequestForm cfg={cfg} pfPurposes={data?.pf_purposes || []} onDone={onClose} />
        )}
      </div>
    </div>
  )
}

/** ISO instant → "HH:MM" wall-clock in `tz` (what the time inputs hold). */
function toHHMM(iso: string, tz?: string | null): string {
  const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }
  try {
    return new Intl.DateTimeFormat('en-GB', { ...opts, timeZone: tz || undefined }).format(new Date(iso))
  } catch {
    return new Intl.DateTimeFormat('en-GB', opts).format(new Date(iso))
  }
}

function to12h(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number)
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-xs text-gray-400 mt-1">{hint}</span>}
    </label>
  )
}

function RequestForm({ cfg, pfPurposes, onDone }: { cfg: RequestTypeConfig; pfPurposes: string[]; onDone: () => void }) {
  const qc = useQueryClient()
  const kind = cfg.key
  const needs = new Set(cfg.needs || [])
  const [date, setDate] = useState(kind === 'attendance_correction' ? '' : today())
  const [endDate, setEndDate] = useState(today())
  const [checkin, setCheckin] = useState('')
  const [checkout, setCheckout] = useState('')
  const [hours, setHours] = useState('')
  const [amount, setAmount] = useState('')
  const [installments, setInstallments] = useState('3')
  const [title, setTitle] = useState(kind === 'pf' ? pfPurposes[0] || '' : '')
  const [details, setDetails] = useState('')
  const [attachment, setAttachment] = useState('')
  const [error, setError] = useState('')

  // Check-in/out correction: load what was actually punched on the picked day, so a
  // forgotten check-out only needs the check-out time (the check-in is filled in).
  const { user } = useAuthStore()
  const correcting = kind === 'attendance_correction'
  const { data: dayRecords, isFetching: loadingDay } = useQuery({
    queryKey: ['attendance', 'my-history', date],
    queryFn: () => getMyHistory(date, date),
    enabled: correcting && !!date,
    staleTime: 0,
  })
  // The session a correction updates is the day's latest one (same rule as the server).
  const session = (dayRecords || [])
    .filter((r) => r.date === date && r.checkin_time)
    .sort((a, b) => (b.checkin_time || '').localeCompare(a.checkin_time || ''))[0]
  const punched = session
    ? {
        checkin: toHHMM(session.checkin_time!, user?.timezone),
        checkout: session.checkout_time ? toHHMM(session.checkout_time, user?.timezone) : '',
      }
    : null
  useEffect(() => {
    if (!correcting || loadingDay) return
    setCheckin(punched?.checkin || '')
    setCheckout(punched?.checkout || '')
  }, [date, loadingDay, session?.checkin_time, session?.checkout_time]) // eslint-disable-line react-hooks/exhaustive-deps

  const mut = useMutation({
    mutationFn: async () => {
      if (kind === 'attendance_correction') {
        // Only send the times being corrected — an unchanged punch stays exactly as recorded.
        const sendIn = checkin && checkin !== punched?.checkin ? checkin : null
        const sendOut = checkout && checkout !== punched?.checkout ? checkout : null
        return createRegularization({ date, checkin_time: sendIn, checkout_time: sendOut, reason: details })
      }
      if (kind === 'overtime') return createOvertime({ date, hours: Number(hours), reason: details })
      return createEmployeeRequest({
        kind,
        start_date: needs.has('start_date') || kind === 'travel' ? date : null,
        end_date: needs.has('end_date') ? endDate : null,
        amount: amount === '' ? null : Number(amount),
        installments: kind === 'loan' ? Number(installments) : null,
        title: title || null,
        details,
        attachment_url: attachment || null,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-requests'] })
      qc.invalidateQueries({ queryKey: ['inbox'] })
      onDone()
    },
    onError: (e: any) => {
      const d = e?.response?.data?.detail
      setError(typeof d === 'string' ? d : Array.isArray(d) ? d.map((x: any) => x.msg).join(', ') : 'Could not submit the request')
    },
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (correcting && !checkin && !checkout) { setError('Enter a check-in and/or check-out time.'); return }
    if (correcting && punched && checkin === punched.checkin && checkout === punched.checkout) {
      setError('These are already your recorded times — change the one that is wrong.'); return
    }
    if (correcting && !punched && (!checkin || !checkout)) {
      setError("You have no attendance on this day — enter both check-in and check-out times."); return
    }
    if (!details.trim()) { setError('Please add a reason / details.'); return }
    mut.mutate()
  }

  const instalmentAmount = kind === 'loan' && Number(amount) > 0 && Number(installments) > 0
    ? Math.round((Number(amount) / Number(installments)) * 100) / 100 : null

  return (
    <form onSubmit={submit} className="p-5 space-y-4">
      <p className="text-sm text-gray-500">{cfg.description}</p>

      {kind === 'attendance_correction' && (
        <>
          <Field label="Date"><input type="date" required max={today()} value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Check-in time"><input type="time" value={checkin} onChange={(e) => setCheckin(e.target.value)} className={inputCls} /></Field>
            <Field label="Check-out time"><input type="time" value={checkout} onChange={(e) => setCheckout(e.target.value)} className={inputCls} /></Field>
          </div>
          {date && !loadingDay && (
            punched ? (
              <p className="text-xs text-blue-800 bg-blue-50 rounded-lg px-3 py-2 -mt-1">
                {punched.checkout
                  ? <>On this day you checked in at <strong>{to12h(punched.checkin)}</strong> and out at <strong>{to12h(punched.checkout)}</strong>. Change the time that was wrong.</>
                  : <>You checked in at <strong>{to12h(punched.checkin)}</strong> but didn't check out. Add your check-out time.</>}
              </p>
            ) : (
              <p className="text-xs text-gray-400 -mt-2">No check-in or check-out recorded on this day — enter both times (in your own timezone).</p>
            )
          )}
          {date && loadingDay && <p className="text-xs text-gray-400 -mt-2">Checking your attendance for this day…</p>}
        </>
      )}

      {kind === 'overtime' && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date"><input type="date" required max={today()} value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} /></Field>
          <Field label="Extra hours"><input type="number" required min={0.25} max={24} step={0.25} value={hours} onChange={(e) => setHours(e.target.value)} className={inputCls} placeholder="e.g. 2" /></Field>
        </div>
      )}

      {kind === 'pf' && (
        <Field label="Request type">
          <select value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls}>
            {pfPurposes.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
      )}

      {needs.has('title') && kind !== 'pf' && (
        <Field label={{ expense: 'What was it for?', official_duty: 'Place / client', travel: 'Destination', general: 'Subject' }[kind] || 'Title'}>
          <input required value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} className={inputCls} />
        </Field>
      )}

      {(needs.has('start_date') || kind === 'travel') && (
        needs.has('end_date') ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label="From"><input type="date" required value={date} onChange={(e) => { setDate(e.target.value); if (e.target.value > endDate) setEndDate(e.target.value) }} className={inputCls} /></Field>
            <Field label="To"><input type="date" required min={date} value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputCls} /></Field>
          </div>
        ) : (
          <Field label={kind === 'medical' || kind === 'expense' ? 'Date of expense' : 'Date'}>
            <input type="date" required max={kind === 'medical' || kind === 'expense' ? today() : undefined} value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
          </Field>
        )
      )}

      {(needs.has('amount') || kind === 'travel') && (
        <div className={kind === 'loan' ? 'grid grid-cols-2 gap-3' : ''}>
          <Field label={kind === 'travel' ? 'Estimated cost (PKR, optional)' : 'Amount (PKR)'}>
            <input type="number" required={needs.has('amount')} min={needs.has('amount') ? 1 : 0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} />
          </Field>
          {kind === 'loan' && (
            <Field label="Monthly instalments" hint={instalmentAmount ? `≈ PKR ${instalmentAmount.toLocaleString()} a month from next month's salary` : undefined}>
              <input type="number" required min={1} max={60} value={installments} onChange={(e) => setInstallments(e.target.value)} className={inputCls} />
            </Field>
          )}
        </div>
      )}
      {kind === 'advance' && <p className="text-xs text-gray-400 -mt-2">Deducted in full from the salary of the month it's approved.</p>}

      <Field label={kind === 'attendance_correction' || kind === 'overtime' ? 'Reason' : 'Details'}>
        <textarea required rows={3} value={details} onChange={(e) => setDetails(e.target.value)} maxLength={5000} className={inputCls} />
      </Field>

      {(kind === 'medical' || kind === 'expense' || kind === 'travel') && (
        <Field label="Receipt / document link (optional)" hint="Paste a link to the receipt (Google Drive, Dropbox, etc.).">
          <input type="url" value={attachment} onChange={(e) => setAttachment(e.target.value)} placeholder="https://…" className={inputCls} />
        </Field>
      )}

      {cfg.flow === 'finance' && <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2">Goes to your manager first, then HR / Payroll for final approval.</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onDone} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
        <button type="submit" disabled={mut.isPending} className="px-4 py-2 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-lg disabled:opacity-60">
          {mut.isPending ? 'Submitting…' : 'Submit Request'}
        </button>
      </div>
    </form>
  )
}
